import lodashMerge from 'lodash/merge';

import rollbarLogger from 'config/rollbar';
import { HeaterSample, SamplePointWithHeaterAddOn } from 'types/models/heater';
import Sample, { SampleDate } from 'types/models/sample';
import { SamplePointId } from 'types/models/samplePoint';

/**
 * Merges samples from multiple sub-sample points of a heater into a single map.
 * Each entry maps a date to a heater sample that combines data from all relevant sub-sample points.
 */
export const mergeHeaterSamples = (
  requestSamplePointIds: SamplePointId[],
  samplesFromAssociatedSamplePoints: Sample[][],
  hostSamplePoint: SamplePointWithHeaterAddOn
): Map<number, HeaterSample> => {
  if (!hostSamplePoint._hidden) {
    rollbarLogger.error('[merge-heater-samples] Could not retrieve sub-sample point ids', { hostSamplePoint });
    return new Map();
  }

  const energySPId = hostSamplePoint._hidden?.heater?.energy;
  const onOffSPId = hostSamplePoint._hidden?.heater?.onOff;
  const temperatureSPId = hostSamplePoint._hidden?.heater?.temperature;

  // The sub-sample points represented by requestSamplePointIds are in the same order as
  // the sub-sample points represented by samplesFromAssociatedSamplePoints.
  // We will use this order to match the samples with the appropriate merge key.
  const mergeKeys: (keyof HeaterSample | undefined)[] = requestSamplePointIds.map(currSamplePointId => {
    switch (currSamplePointId) {
      case energySPId:
        return 'energy';
      case onOffSPId:
        return 'onOff';
      case temperatureSPId:
        return 'temperature';
      default:
        return;
    }
  });

  const samplesByDate = new Map<SampleDate, HeaterSample>();
  const mergeSubSampleIntoMap = (mergeKey: keyof HeaterSample, subSample: Sample) => {
    const date: SampleDate = subSample.date;
    const existingRecord: HeaterSample | undefined = samplesByDate.get(date);
    const emptyRecord: HeaterSample = { date, seqNo: subSample.extraValues.seqNo };
    const updatedRecord: HeaterSample = lodashMerge(existingRecord || emptyRecord, { [mergeKey]: subSample });
    samplesByDate.set(date, updatedRecord);
  };

  samplesFromAssociatedSamplePoints.forEach((samplesOfOneSamplePoint, index) => {
    if (samplesOfOneSamplePoint.length === 0) {
      return;
    }
    const mergeKey = mergeKeys[index];
    if (!mergeKey) return;
    samplesOfOneSamplePoint.forEach(sample => mergeSubSampleIntoMap(mergeKey, sample));
  });

  return samplesByDate;
};